import React, { useEffect, useState, useCallback } from "react";
import api, { extractError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, LoadingState, EmptyState } from "@/components/Common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { fmtDate } from "@/lib/format";
import { FileText, Upload, Download, Loader2, Trash2, Landmark, FolderKanban } from "lucide-react";
import { toast } from "sonner";

const CATS = ["Consenso", "Convenzione", "Attestato", "Statuto", "Relazione", "Verbale", "Allegato", "Altro"];

const Documents = () => {
  const { can } = useAuth();
  const [items, setItems] = useState([]);
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [titolo, setTitolo] = useState("");
  const [categoria, setCategoria] = useState("Allegato");
  const [saving, setSaving] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(null);
  const canCases = can("cases.read");

  const load = useCallback(() => {
    setLoading(true);
    const reqs = [api.get("/documents").then(({ data }) => setItems(data)).catch((e) => console.error("Errore documenti", e))];
    if (canCases) reqs.push(api.get("/cases?limit=200&ordina=data&direzione=desc").then(({ data }) => setCases(data.items)).catch((e) => console.error("Errore pratiche", e)));
    Promise.all(reqs).finally(() => setLoading(false));
  }, [canCases]);
  useEffect(() => { load(); }, [load]);

  const istituzionali = items.filter((d) => !d.case_id);

  const upload = async () => {
    if (!file) { toast.error("Seleziona un file"); return; }
    setSaving(true);
    const fd = new FormData();
    fd.append("file", file); fd.append("titolo", titolo || file.name); fd.append("categoria", categoria);
    try { await api.post("/documents", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Documento caricato"); setOpen(false); setFile(null); setTitolo(""); load(); }
    catch (e) { toast.error(extractError(e)); } finally { setSaving(false); }
  };
  const download = (d) => api.get(`/documents/${d.id}/download`, { responseType: "blob" }).then((r) => { const u = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = u; a.download = d.nome_file || "documento"; a.click(); URL.revokeObjectURL(u); });
  const del = async (d) => { if (!window.confirm("Eliminare il documento?")) return; await api.delete(`/documents/${d.id}`); toast.success("Documento eliminato"); load(); };

  const exportScheda = async (c) => {
    setPdfBusy(c.id);
    try {
      const r = await api.get(`/cases/${c.id}/pdf`, { responseType: "blob" });
      const u = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = u; a.download = `scheda_${c.numero}.pdf`; a.click(); URL.revokeObjectURL(u);
      toast.success(`Scheda ${c.numero} esportata`);
    } catch (e) { toast.error(extractError(e, "Esportazione non riuscita")); } finally { setPdfBusy(null); }
  };

  return (
    <div>
      <PageHeader icon={FileText} title="Documenti" description="Archivio documentale (accesso protetto)"
        actions={can("documents.write") && <Button onClick={() => setOpen(true)} className="bg-[color:var(--brand-orange-600)] hover:bg-[color:var(--brand-orange-500)] text-white" data-testid="upload-doc-button"><Upload className="h-4 w-4 mr-2" /> Carica documento</Button>} />

      {loading ? <LoadingState /> : (
        <Tabs defaultValue="istituzionali">
          <TabsList>
            <TabsTrigger value="istituzionali" data-testid="tab-istituzionali"><Landmark className="h-4 w-4 mr-1.5" /> Docs Istituzionali</TabsTrigger>
            {canCases && <TabsTrigger value="schede" data-testid="tab-schede"><FolderKanban className="h-4 w-4 mr-1.5" /> Schede Pratica</TabsTrigger>}
          </TabsList>

          {/* Documenti istituzionali (non collegati a una pratica) */}
          <TabsContent value="istituzionali" className="mt-4">
            <p className="text-sm text-muted-foreground mb-3">Documenti afferenti all'attività istituzionale dell'Ente (statuti, convenzioni, attestati…).</p>
            {istituzionali.length === 0 ? <EmptyState title="Nessun documento istituzionale" /> : (
              <Card className="divide-y" data-testid="docs-istituzionali">
                {istituzionali.map((d) => (
                  <div key={d.id} className="p-4 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0"><FileText className="h-5 w-5 text-[color:var(--brand-blue-600)] shrink-0" /><div className="min-w-0"><div className="font-medium truncate">{d.titolo}</div><div className="text-xs text-muted-foreground">{d.categoria} · {(d.dimensione / 1024).toFixed(0)} KB · {fmtDate(d.created_at)}</div></div></div>
                    <div className="flex gap-1"><Button variant="ghost" size="sm" onClick={() => download(d)} data-testid="doc-download"><Download className="h-4 w-4" /></Button>{can("documents.write") && <Button variant="ghost" size="sm" onClick={() => del(d)}><Trash2 className="h-4 w-4 text-[color:var(--brand-red-600)]" /></Button>}</div>
                  </div>
                ))}
              </Card>
            )}
          </TabsContent>

          {/* Schede pratica esportabili */}
          {canCases && (
            <TabsContent value="schede" className="mt-4">
              <p className="text-sm text-muted-foreground mb-3">Schede delle singole pratiche: esporta il PDF di riepilogo di ciascuna.</p>
              {cases.length === 0 ? <EmptyState title="Nessuna pratica" /> : (
                <Card className="divide-y" data-testid="docs-schede-pratica">
                  {cases.map((c) => (
                    <div key={c.id} className="p-4 flex items-center justify-between gap-3" data-testid="scheda-row">
                      <div className="min-w-0">
                        <div className="font-semibold text-sm">{c.numero}
                          {c.persona_nome && <span className="ml-2 font-semibold text-[color:var(--brand-blue-700)]" data-testid="scheda-persona">{c.persona_nome}</span>}
                        </div>
                        <div className="text-xs text-muted-foreground truncate">
                          {c.servizio_nome}
                          {c.gestore_nome && <> · <span className="font-semibold text-[color:var(--brand-green-700)]" data-testid="scheda-gestore">Gestore: {c.gestore_nome}</span></>}
                        </div>
                      </div>
                      <Button variant="outline" size="sm" onClick={() => exportScheda(c)} disabled={pdfBusy === c.id} data-testid="scheda-export-pdf">
                        {pdfBusy === c.id ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />} Esporta PDF
                      </Button>
                    </div>
                  ))}
                </Card>
              )}
            </TabsContent>
          )}
        </Tabs>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display">Carica documento istituzionale</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>File (max 15 MB)</Label><Input type="file" onChange={(e) => setFile(e.target.files[0])} data-testid="doc-file-input" /></div>
            <div><Label>Titolo</Label><Input value={titolo} onChange={(e) => setTitolo(e.target.value)} placeholder="Facoltativo" /></div>
            <div><Label>Categoria</Label><Select value={categoria} onValueChange={setCategoria}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
          </div>
          <DialogFooter className="gap-2"><Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button><Button onClick={upload} disabled={saving} className="bg-[color:var(--brand-orange-600)] hover:bg-[color:var(--brand-orange-500)] text-white">{saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Carica</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Documents;
